import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircleIcon, AlertCircleIcon, MinusCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
interface ExtractedField {
  label: string;
  value: string;
  confidence: 'high' | 'medium' | 'low';
}
interface FieldGroup {
  title: string;
  fields: ExtractedField[];
}
interface FieldExtractionProps {
  fileName: string;
  onBack: () => void;
}
export function FieldExtraction({
  fileName,
  onBack
}: FieldExtractionProps) {
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([{
    title: 'Personal Information',
    fields: [{
      label: 'Full Name',
      value: 'John Michael Anderson',
      confidence: 'high'
    }, {
      label: 'Date of Birth',
      value: '03/15/1985',
      confidence: 'high'
    }, {
      label: 'ID Number',
      value: 'DL-4829-3847-2910',
      confidence: 'medium'
    }]
  }, {
    title: 'Contact Information',
    fields: [{
      label: 'Email Address',
      value: 'john.anderson@email.com',
      confidence: 'high'
    }, {
      label: 'Phone Number',
      value: '+1 (555) 123-4567',
      confidence: 'high'
    }, {
      label: 'Address',
      value: '742 Evergreen Terrace, Springfield, IL 62701',
      confidence: 'medium'
    }]
  }, {
    title: 'Document Details',
    fields: [{
      label: 'Document Type',
      value: 'Driver License',
      confidence: 'high'
    }, {
      label: 'Issue Date',
      value: '01/10/2023',
      confidence: 'high'
    }, {
      label: 'Expiration Date',
      value: '01/10/2028',
      confidence: 'low'
    }]
  }]);
  const handleFieldChange = (groupIndex: number, fieldIndex: number, newValue: string) => {
    setFieldGroups(prev => {
      const updated = [...prev];
      updated[groupIndex].fields[fieldIndex].value = newValue;
      return updated;
    });
  };
  const handleSave = () => {
    toast.success('Document fields saved successfully!', {
      description: 'All extracted information has been confirmed and saved.'
    });
  };
  const getConfidenceColor = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return 'text-green-600 bg-green-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-red-600 bg-red-50';
    }
  };
  const getConfidenceIcon = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return <CheckCircleIcon className="w-4 h-4" data-id="element-30" />;
      case 'medium':
        return <AlertCircleIcon className="w-4 h-4" data-id="element-31" />;
      case 'low':
        return <MinusCircleIcon className="w-4 h-4" data-id="element-32" />;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="w-full max-w-4xl mx-auto" data-id="element-33">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-id="element-34">
        <div className="flex items-center justify-between mb-6" data-id="element-35">
          <div data-id="element-36">
            <h2 className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-37">
              Review Extracted Fields
            </h2>
            <p className="text-gray-600 text-sm" data-id="element-38">From: {fileName}</p>
          </div>
          <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-39">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-40" />
            Back
          </button>
        </div>

        <div className="space-y-6" data-id="element-41">
          {fieldGroups.map((group, groupIndex) => <motion.div key={group.title} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: groupIndex * 0.1
        }} className="border border-gray-200 rounded-lg p-6" data-id="element-42">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-43">
                {group.title}
              </h3>
              <div className="space-y-4" data-id="element-44">
                {group.fields.map((field, fieldIndex) => <div key={field.label} className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-3 items-start" data-id="element-45">
                    <label className="text-sm font-medium text-gray-700 pt-2" data-id="element-46">
                      {field.label}
                    </label>
                    <input type="text" value={field.value} onChange={e => handleFieldChange(groupIndex, fieldIndex, e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-47" />
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getConfidenceColor(field.confidence)}`} data-id="element-48">
                      {getConfidenceIcon(field.confidence)}
                      <span className="capitalize" data-id="element-49">{field.confidence}</span>
                    </div>
                  </div>)}
              </div>
            </motion.div>)}
        </div>

        <div className="mt-8 flex gap-3" data-id="element-50">
          <button onClick={onBack} className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-51">
            Cancel
          </button>
          <button onClick={handleSave} className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors" data-id="element-52">
            Confirm & Save
          </button>
        </div>
      </div>
    </motion.div>;
}